import React, { FC, PropsWithChildren, isValidElement, Children, useState, cloneElement, useEffect, useRef, useCallback } from 'react';
import { ScrollView } from '@tarojs/components';
import classNames from 'classnames';
import { uuid } from '../../common/utils';
import View from '../view';
import TabPane from './TabPane';

interface ITabItem {
  key: string;
  title: string;
}

interface ITabsProps {
  className?: string;
  defaultActiveKey?: string;
  activeKey?: string;
  animated?: boolean;
  scroll?: boolean;
  onChange?: (key: string) => void;
}

const parseTabList = (children: React.ReactNode) => {
  return (
    Children.map(children, (node) => {
      if (isValidElement(node)) {
        const { key, props: { tab = '' } = {} } = node;
        return {
          key: key !== undefined ? String(key) : undefined,
          title: tab,
        };
      }
      return null;
    }) || []
  ).filter((tab) => tab) as ITabItem[];
};

const getTabIndex = (tabs: ITabItem[], activeKey?: string) => tabs.findIndex(({ key }) => key === activeKey);

const Tabs: FC<PropsWithChildren<ITabsProps>> & { TabPane: typeof TabPane } = ({
  className = '',
  defaultActiveKey,
  activeKey: propsActiveKey,
  children,
  onChange,
  animated = false,
  scroll = false,
}) => {
  const tabs = parseTabList(children);
  const [{ key: firstTabKey = undefined } = {}] = tabs;
  const tabIdRef = useRef(uuid());
  const [activeKey, setActiveKey] = useState(propsActiveKey || defaultActiveKey || firstTabKey);
  const [scrollViewId, setScrollViewId] = useState('');

  const rootClass = classNames('ink-tabs', className);

  const updateScrollViewId = useCallback(
    (key?: string) => {
      if (!scroll) {
        return;
      }
      const idx = getTabIndex(tabs, key);
      const index = Math.max(idx - 1, 0);
      const indexKey = tabs[index]?.key;
      if (indexKey) {
        setScrollViewId(`tab-${tabIdRef?.current}-${indexKey}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scroll, JSON.stringify(tabs)],
  );

  const onTabClick = (key: string) => {
    if (typeof propsActiveKey === 'undefined') {
      setActiveKey(key);
      updateScrollViewId(key);
    }
    if (onChange && typeof onChange === 'function') {
      onChange(key);
    }
  };

  useEffect(() => {
    if (typeof propsActiveKey !== 'undefined') {
      setActiveKey(propsActiveKey);
      updateScrollViewId(propsActiveKey);
    }
  }, [propsActiveKey, updateScrollViewId]);

  const tabItems = tabs.map(({ key, title }) => (
    <View
      id={`tab-${tabIdRef?.current}-${key}`}
      key={key}
      onClick={() => {
        onTabClick(key);
      }}
      className={classNames('ink-tabs__header-item', { 'ink-tabs__header-item--active': key === activeKey, 'ink-tabs__header-item--scroll': scroll })}
    >
      {title}
    </View>
  ));

  const tabPanes = Children.map(children, (node) => (isValidElement(node) ? cloneElement(node, { id: node.key, activeKey }) : node));

  return (
    <View className={rootClass}>
      {scroll ? (
        <ScrollView enableFlex className="ink-tabs__header" scrollX scrollWithAnimation scrollIntoView={scrollViewId}>
          {tabItems}
        </ScrollView>
      ) : (
        <View className="ink-tabs__header">{tabItems}</View>
      )}
      <View className={classNames('ink-tabs__body', { 'ink-tabs__body--animated': animated })}>
        {animated ? (
          <View className="ink-tabs__track" style={{ transform: `translate3d(-${getTabIndex(tabs, activeKey) * 100}%,0,0)`, transitionDuration: '0.3s' }}>
            {tabPanes}
          </View>
        ) : (
          tabPanes
        )}
      </View>
    </View>
  );
};

Tabs.TabPane = TabPane;

export default Tabs;
